import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  DeliveryStop, StopStatus, DeliverStopInput, FailStopInput,
  DriverDashboard, DriverPerformance,
  listStopsForRoute, listMyDriverStops, getDriverDashboard, getDriverPerformance,
  deliverStop, failStopApi, updateStopStatus, getStop,
} from '../services/deliveriesApi';

interface DeliveriesState {
  stops: DeliveryStop[];
  current: DeliveryStop | null;
  dashboard: DriverDashboard | null;
  performance: DriverPerformance | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchStopsForRoute: (routeId: number | string) => Promise<void>;
  fetchMyStops: (filters?: { status?: StopStatus; from?: string; to?: string }) => Promise<void>;
  fetchDashboard: () => Promise<void>;
  fetchPerformance: (days?: number) => Promise<void>;
  fetchStop: (id: number | string) => Promise<DeliveryStop>;
  deliver: (id: number | string, input?: DeliverStopInput) => Promise<DeliveryStop>;
  fail: (id: number | string, input: FailStopInput) => Promise<DeliveryStop>;
  setStatus: (id: number | string, status: StopStatus) => Promise<DeliveryStop>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useDeliveriesStore = create<DeliveriesState>()(
  devtools(
    (set) => ({
      stops: [],
      current: null,
      dashboard: null,
      performance: null,
      loading: false,
      saving: false,
      error: null,
      fetchStopsForRoute: async (routeId) => {
        set({ loading: true, error: null });
        try { set({ stops: await listStopsForRoute(routeId), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchMyStops: async (filters) => {
        set({ loading: true, error: null });
        try { set({ stops: await listMyDriverStops(filters), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchDashboard: async () => {
        try { set({ dashboard: await getDriverDashboard() }); }
        catch (err) { set({ error: toErr(err) }); }
      },
      fetchPerformance: async (days) => {
        try { set({ performance: await getDriverPerformance(days) }); }
        catch (err) { set({ error: toErr(err) }); }
      },
      fetchStop: async (id) => {
        const stop = await getStop(id);
        set({ current: stop });
        return stop;
      },
      deliver: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await deliverStop(id, input ?? {});
          set((s) => ({
            stops: s.stops.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      fail: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await failStopApi(id, input);
          set((s) => ({
            stops: s.stops.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      setStatus: async (id, status) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateStopStatus(id, status);
          set((s) => ({
            stops: s.stops.map((it) => (String(it.id) === String(id) ? updated : it)),
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'deliveries-store' }
  )
);
